"use client";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DiamondIcon from "@mui/icons-material/Diamond";
import { useState, useEffect } from "react";
import { useAxiosFetch } from "../../hooks/useAxiosFetch.js";
import { axiosRequest } from "../../utility/axios.js";
import { useSelector } from "react-redux";
import ChipToken, { getChipColor } from "../../components/ChipToken";

const CHIP_DENOMINATIONS = [100, 500, 1000, 5000, 10000, 25000];

export default function ChipsMaster() {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const { data, refetch } = useAxiosFetch("GET", "/chips", token);
  const [onTableChips, setOnTableChips] = useState({});

  const allChips = data || [];
  const regularChips = allChips.filter((c) => (c.chip_type || "REGULAR") !== "PROMO");
  const promoChips   = allChips.filter((c) => c.chip_type === "PROMO");

  useEffect(() => {
    axiosRequest("GET", "/shift-management/current-summary", token)
      .then(res => setOnTableChips(res?.data?.on_table_chips || {}))
      .catch(() => {});
  }, [token]);

  // Regular inventory: counter + on-table
  const inventoryMap = {};
  regularChips.forEach(c => {
    inventoryMap[c.denomination] = {
      counter: c.quantity,
      onTable: Number(onTableChips[String(c.denomination)] || 0),
    };
  });
  Object.keys(onTableChips).filter(k => !String(k).startsWith("P")).forEach(d => {
    if (!inventoryMap[Number(d)]) {
      inventoryMap[Number(d)] = { counter: 0, onTable: Number(onTableChips[d]) };
    }
  });

  // Promo inventory: counter + on-table (P-prefixed keys)
  const promoInventoryMap = {};
  promoChips.forEach(c => {
    promoInventoryMap[c.denomination] = {
      counter: c.quantity,
      onTable: Number(onTableChips[`P${c.denomination}`] || 0),
    };
  });
  Object.keys(onTableChips).filter(k => String(k).startsWith("P")).forEach(k => {
    const d = Number(k.slice(1));
    if (!promoInventoryMap[d]) {
      promoInventoryMap[d] = { counter: 0, onTable: Number(onTableChips[k]) };
    }
  });

  const totalValue = Object.entries(inventoryMap).reduce(
    (sum, [d, { counter, onTable }]) => sum + Number(d) * (counter + onTable), 0
  );
  const promoTotalValue = Object.entries(promoInventoryMap).reduce(
    (sum, [d, { counter, onTable }]) => sum + Number(d) * (counter + onTable), 0
  );

  const [open, setOpen] = useState(false);
  const [denomination, setDenomination] = useState("");
  const [quantity, setQuantity] = useState("");
  const [chipType, setChipType] = useState("REGULAR");

  const handleAdd = async () => {
    const denom = parseInt(denomination, 10);
    const qty   = parseInt(quantity, 10);
    if (!denom || isNaN(denom) || !qty || isNaN(qty)) return;

    await axiosRequest("POST", "/chip", token, {
      denomination: denom,
      quantity: qty,
      chip_type: chipType,
    });

    setOpen(false);
    setDenomination("");
    setQuantity("");
    setChipType("REGULAR");
    refetch();
  };

  const handleStatusChange = async (chipId, value) => {
    await axiosRequest("PUT", "/chip/status", token, {
      _id: chipId,
      status: value === "true",
    });
    refetch();
  };

  const ChipTable = ({ inventoryEntries, isPromo = false }) => (
    <Paper elevation={4} sx={{ borderRadius: "12px", overflow: "hidden", mb: isPromo ? 0 : 4 }}>
      <Table style={{ width: "100%" }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: isPromo ? "#7b1fa215" : "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Denomination</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Counter</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>On Table</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Total Qty</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Total Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {inventoryEntries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                No {isPromo ? "promotional " : ""}chip denominations found
              </TableCell>
            </TableRow>
          ) : (
            inventoryEntries
              .sort((a, b) => Number(a[0]) - Number(b[0]))
              .map(([denom, { counter, onTable }]) => {
                const d = Number(denom);
                const total = counter + onTable;
                const cc = isPromo ? { bg: "#7b1fa2", text: "#fff" } : getChipColor(d);
                return (
                  <TableRow key={d} hover sx={{ backgroundColor: cc.bg + "18" }}>
                    <TableCell style={{ textAlign: "center" }}>
                      <ChipToken denomination={d} size={40} isPromo={isPromo} />
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>{counter}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>{onTable}</TableCell>
                    <TableCell style={{ textAlign: "center" }}><strong>{total}</strong></TableCell>
                    <TableCell style={{ textAlign: "center" }}>{(d * total).toLocaleString()}</TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 13 }, pb: 8 }}>
      {/* HEADER */}
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight={600}>Chips Master</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ borderRadius: "8px", px: 3 }}
          onClick={() => setOpen(true)}
        >
          Add Denomination
        </Button>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* SUMMARY CARDS */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper elevation={4} sx={{ p: 4, borderRadius: "12px", background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
            <Typography variant="h6">Regular Chips Value</Typography>
            <Typography variant="h3" mt={2}>{totalValue.toLocaleString()}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={4} sx={{ p: 4, borderRadius: "12px", background: "linear-gradient(135deg,#6a0080,#9c27b0)", color: "#fff" }}>
            <Typography variant="h6">Promo Chips Value</Typography>
            <Typography variant="h3" mt={2}>{promoTotalValue.toLocaleString()}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={4} sx={{ p: 4, borderRadius: "12px", background: "linear-gradient(135deg,#11998e,#38ef7d)", color: "#fff" }}>
            <Typography variant="h6">Total Denominations</Typography>
            <Typography variant="h3" mt={2}>{allChips.length}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* REGULAR CHIPS TABLE */}
      <Typography variant="h6" fontWeight={700} mb={2}>Regular Chips</Typography>
      <ChipTable inventoryEntries={Object.entries(inventoryMap)} isPromo={false} />

      {/* PROMOTIONAL CHIPS TABLE */}
      <Box display="flex" alignItems="center" gap={1} mb={2} mt={4}>
        <DiamondIcon sx={{ color: "#ba68c8", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={700}>Promotional Chips</Typography>
        <Chip label="PROMO" size="small" sx={{ bgcolor: "#ba68c8", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
        <Typography variant="caption" color="text.secondary">— cannot be withdrawn</Typography>
      </Box>
      <ChipTable inventoryEntries={Object.entries(promoInventoryMap)} isPromo={true} />

      {/* ADD DENOMINATION MODAL */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 420, bgcolor: "#1c2433", p: 4, borderRadius: "12px", mx: "auto", mt: "10%" }}>
          <Typography variant="h6" mb={3}>Add New Denomination</Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Chip Type</InputLabel>
            <Select
              value={chipType}
              label="Chip Type"
              onChange={(e) => setChipType(e.target.value)}
            >
              <MenuItem value="REGULAR">Regular</MenuItem>
              <MenuItem value="PROMO">Promotional (PROMO)</MenuItem>
            </Select>
          </FormControl>

          <Select
            fullWidth displayEmpty
            value={denomination}
            onChange={(e) => setDenomination(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>Select Denomination</MenuItem>
            {CHIP_DENOMINATIONS.map((d) => (
              <MenuItem key={d} value={String(d)}>{d.toLocaleString()}</MenuItem>
            ))}
          </Select>

          <TextField
            fullWidth label="Quantity" type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button fullWidth variant="contained" onClick={handleAdd}>Save</Button>
        </Box>
      </Modal>
    </Box>
  );
}
